import { useLayoutEffect, useRef, type RefObject } from 'react';
import type { Bulle } from '../partage/api/types';
import { ECRIRE_UNE_NOTE, NOTES } from '../partage/vocabulaire';
import { useNoteEcrite } from './ChampsNotes';
import './NoteDeLaFiche.css';

/**
 * La note de la fiche Note (chantier types-de-fiche, modification 03 ; maquette du carnet
 * `fiche-quatre-types.html`, `.ecrit`) : en grand, écrite à la main là où l'ambiance écrit à la
 * main, à même la feuille, et toujours prête à écrire. Vide, la feuille attend, « Écrire une
 * note… » en invite discrète. Elle s'enregistre comme les notes de la fiche Classique
 * (`useNoteEcrite`). Remise à neuf pour chaque bulle : un brouillon ne passe pas d'une bulle à
 * l'autre.
 */
export function NoteDeLaFiche({ bulle, onChanger }: { bulle: Bulle; onChanger: (champs: Partial<Bulle>) => void }) {
  return <FeuilleEcrite key={`note-${bulle.id}`} valeur={bulle.notes} onChanger={(notes) => onChanger({ notes })} />;
}

function FeuilleEcrite({ valeur, onChanger }: { valeur: string | null; onChanger: (valeur: string | null) => void }) {
  const note = useNoteEcrite(valeur, onChanger);
  const champ = useRef<HTMLTextAreaElement>(null);
  useLignesCalees(champ);

  return (
    <textarea
      ref={champ}
      className="fiche-note-ecrit"
      data-tuto="champ-notes"
      aria-label={NOTES}
      value={note.brouillon}
      placeholder={ECRIRE_UNE_NOTE}
      onChange={(e) => note.ecrire(e.target.value)}
      onBlur={note.quitter}
    />
  );
}

/**
 * Les lignes du papier tombent sous l'écriture : chaque ligne écrite occupe une ligne tracée
 * (`--ligne-de-la-fiche`). Le papier de la fiche se cale sur le haut de la note
 * (`--calage-des-lignes`, lu par `.fiche--papier-lignes`), et se recale quand ce qui est
 * au-dessus change de hauteur ou que la fiche défile : la feuille défile avec ce qui est écrit.
 */
function useLignesCalees(champ: RefObject<HTMLTextAreaElement | null>) {
  useLayoutEffect(() => {
    const ecrit = champ.current;
    const fiche = ecrit?.closest<HTMLElement>('.fiche');
    // Ce qui défile : le corps de la fiche de l'ordinateur, ou le milieu de celle du téléphone.
    const corps = ecrit?.closest<HTMLElement>('.fiche-corps, .fiche-pouce-defile');
    if (!ecrit || !fiche) return;

    const caler = () => {
      const pas = parseFloat(getComputedStyle(fiche).getPropertyValue('--ligne-de-la-fiche'));
      if (!pas) return;
      const haut = ecrit.getBoundingClientRect().top + parseFloat(getComputedStyle(ecrit).paddingTop);
      const decalage = haut - fiche.getBoundingClientRect().top - fiche.clientTop;
      fiche.style.setProperty('--calage-des-lignes', `${((decalage % pas) + pas) % pas}px`);
    };

    caler();
    const observateur = new ResizeObserver(caler);
    observateur.observe(fiche);
    if (corps?.firstElementChild) observateur.observe(corps.firstElementChild);
    corps?.addEventListener('scroll', caler, { passive: true });
    return () => {
      observateur.disconnect();
      corps?.removeEventListener('scroll', caler);
      fiche.style.removeProperty('--calage-des-lignes');
    };
  }, [champ]);
}
